import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { DiscountTag } from 'src/assets/discount-tag';

type SpecialOfferTagProps = {
  hideSalesLanguage: boolean;
};

/**
 * Static special offer tag - displays a simple "Special Offer" or "Offer" label.
 */
export const SpecialOfferTag = ({ hideSalesLanguage }: SpecialOfferTagProps): JSX.Element => {
  const text = hideSalesLanguage ? 'Offer' : 'Special Offer';

  return (
    <SpecialOfferWrapper>
      <DiscountTag width={12} />
      <StaticText>{text}</StaticText>
    </SpecialOfferWrapper>
  );
};

type SpecialOfferRotatorProps = {
  specialNames: string[];
  hideSalesLanguage: boolean;
};

/**
 * Rotates through multiple special offer titles with a sliding animation.
 * Uses randomized initial delay and intervals to prevent all cards from animating simultaneously.
 */
export const SpecialOfferRotator = ({ specialNames, hideSalesLanguage }: SpecialOfferRotatorProps): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const isMountedRef = useRef(true);

  // Set random starting index when specialNames becomes available
  useEffect(() => {
    if (specialNames.length > 0) {
      setCurrentIndex(Math.floor(Math.random() * specialNames.length));
    }
  }, [specialNames.length]);

  useEffect(() => {
    isMountedRef.current = true;

    if (specialNames.length <= 1) {
      return undefined;
    }

    // Random initial delay to desynchronize animations across multiple cards
    const initialDelay = Math.random() * 3000;

    let intervalTimeoutId: NodeJS.Timeout | undefined;
    let animationTimeoutId: NodeJS.Timeout | undefined;

    const scheduleNextTransition = (): void => {
      // Random interval for each transition
      const randomInterval = 2500 + Math.random() * 5000;

      intervalTimeoutId = setTimeout(() => {
        if (!isMountedRef.current) {
          return;
        }

        setIsAnimating(true);
        animationTimeoutId = setTimeout(() => {
          if (!isMountedRef.current) {
            return;
          }

          setCurrentIndex((prevIndex) => (prevIndex + 1) % specialNames.length);
          setIsAnimating(false);
          // Schedule the next transition with a new random interval
          scheduleNextTransition();
        }, 500);
      }, randomInterval);
    };

    // Start the cycle after initial delay
    const initialTimeout = setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }
      scheduleNextTransition();
    }, initialDelay);

    return () => {
      isMountedRef.current = false;
      clearTimeout(initialTimeout);
      if (intervalTimeoutId) {
        clearTimeout(intervalTimeoutId);
      }
      if (animationTimeoutId) {
        clearTimeout(animationTimeoutId);
      }
    };
  }, [specialNames.length]);

  const defaultText = hideSalesLanguage ? 'Offer' : 'Special Offer';
  // Use modulo to ensure valid index when specialNames shrinks before effect resets currentIndex
  const displayText = specialNames.length > 0 ? specialNames[currentIndex % specialNames.length] : defaultText;
  const additionalOffersCount = Math.min(specialNames.length - 1, 9);

  return (
    <SpecialOfferWrapper>
      <DiscountTag width={12} />
      <TextContainer>
        <AnimatedText $isAnimating={isAnimating}>
          <TruncatedText>{displayText}</TruncatedText>
          {additionalOffersCount > 0 && <OfferCountBadge>+{additionalOffersCount}</OfferCountBadge>}
        </AnimatedText>
      </TextContainer>
    </SpecialOfferWrapper>
  );
};

const slideUp = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

const slideIn = keyframes`
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const SpecialOfferWrapper = styled.div`
  align-items: center;
  color: #0d865e;
  display: flex;
  gap: 4px;
  justify-content: var(--justify-content);
  position: relative;
  width: 100%;
  font-weight: 600;
`;

const TextContainer = styled.div`
  flex: 1;
  min-height: 13px;
  min-width: 0;
  overflow: hidden;
  position: relative;
`;

const AnimatedText = styled.span<{ $isAnimating: boolean }>`
  align-items: center;
  animation: ${({ $isAnimating }) => ($isAnimating ? slideUp : slideIn)} 0.5s ease-in-out;
  display: grid;
  grid-template-columns: minmax(0, max-content) auto;
`;

const TruncatedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OfferCountBadge = styled.span`
  width: 100%;
  height: 100%;
  padding-left: 6px;
  padding-right: 6px;
  padding-top: 2px;
  padding-bottom: 2px;
  background: #ceeedb;
  overflow: hidden;
  border-radius: 999px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  display: inline-flex;
  color: #0d865e;
  width: 24px;
  margin-left: 4px;
  font-size: 10px;
  height: 15px;
`;

const StaticText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
